import { Box, TextField } from '@mui/material';
import React from 'react';
import { htmlHexForColorInput, isHtmlHexColor, normalizeHtmlHex } from '../helpers/htmlColor';
import { resolveJobColorPreview } from '../helpers/jobDisplay';

/**
 * Seletor de cor que grava em notação HTML (#rrggbb).
 * Com preserveLiteral, valores não-hex ficam visíveis até o usuário usar o picker.
 */
export default function HtmlColorField({
    label = 'Cor',
    value = '',
    onChange,
    preserveLiteral = false,
    fullWidth = true,
    error = false,
    helperText,
    disabled = false,
}) {
    const trimmed = String(value || '').trim();
    const isHex = isHtmlHexColor(trimmed);
    const showLiteral = preserveLiteral && trimmed && !isHex;
    const pickerHex = htmlHexForColorInput(trimmed);

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                width: fullWidth ? '100%' : 'auto',
            }}
        >
            <Box
                component="input"
                type="color"
                value={pickerHex}
                disabled={disabled}
                onChange={(e) => {
                    if (typeof onChange === 'function') {
                        onChange(normalizeHtmlHex(e.target.value));
                    }
                }}
                aria-label={label}
                title="Selecionar cor"
                sx={{
                    width: 48,
                    height: 56,
                    mt: 0,
                    p: 0.25,
                    border: '1px solid',
                    borderColor: error ? 'error.main' : 'divider',
                    borderRadius: 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    bgcolor: 'transparent',
                    flexShrink: 0,
                }}
            />
            {showLiteral ? (
                <TextField
                    label={label}
                    value={trimmed}
                    fullWidth={fullWidth}
                    error={error}
                    disabled={disabled}
                    InputProps={{ readOnly: true }}
                    helperText={helperText || 'Cor literal mantida. Use o seletor para gravar em #RRGGBB.'}
                />
            ) : (
                <TextField
                    label={label}
                    value={isHex ? normalizeHtmlHex(trimmed) : trimmed}
                    fullWidth={fullWidth}
                    error={error}
                    disabled={disabled}
                    placeholder="#rrggbb"
                    onChange={(e) => {
                        if (typeof onChange !== 'function') {
                            return;
                        }
                        const next = e.target.value.trim();
                        if (next === '') {
                            onChange('');
                            return;
                        }
                        if (isHtmlHexColor(next)) {
                            onChange(normalizeHtmlHex(next));
                            return;
                        }
                        onChange(next);
                    }}
                    onBlur={() => {
                        if (typeof onChange !== 'function') {
                            return;
                        }
                        if (trimmed && isHtmlHexColor(trimmed)) {
                            onChange(normalizeHtmlHex(trimmed));
                        }
                    }}
                    helperText={helperText || 'Notação HTML (#RRGGBB)'}
                />
            )}
        </Box>
    );
}

export function ColorSwatchLabel({ color, label, emptyLabel = '—' }) {
    const preview = resolveJobColorPreview(color);
    const display = String(label || color || '').trim() || emptyLabel;

    if (!preview) {
        return display;
    }

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '4px',
                    bgcolor: preview.css === 'transparent' ? 'transparent' : preview.css,
                    border: '1px solid',
                    borderColor: preview.needsBorder || preview.css === 'transparent' ? 'divider' : 'transparent',
                    backgroundImage: preview.css === 'transparent'
                        ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                        : 'none',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
                    flexShrink: 0,
                }}
            />
            <span>{display}</span>
        </Box>
    );
}
